import CloseIcon from '@mui/icons-material/Close'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import PersonIcon from '@mui/icons-material/Person'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Select,
  SortDirection,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { orange } from '@mui/material/colors'
import {
  CompanyUsersRelation,
  CompanyUsersStatus,
  CompanyUserWithUserAndRole
} from '@shared/types/companies.types'
import { Permission } from '@shared/types/roles.types'
import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import EmptyInvitations from '../../assets/images/EmptyInvitation.svg'
import ContainedAvatar from '../../components/ContainedAvatar'
import EmptySearchResults from '../../components/EmptySearchResults'
import SearchInput from '../../components/forms/SearchInput'
import TableLoader from '../../components/loaders/TableLoader'
import RoleChip from '../../components/RoleChip'
import { useAuthStore } from '../../hooks/useAuthStore'
import {
  useRejectInvitationMutation,
  useResendInvitationMutation
} from '../../hooks/useMutations/useCompaniesMutations'
import {
  useCompanyRoles,
  useCompanyUser,
  useCompanyUsers
} from '../../hooks/useQueries/useCompanies'
import { useSnackbar } from '../../hooks/useSnackbar'
import { parseTimestamp } from '../../utils/date.utils'
import { getAvatarName, isDefaultRole } from '../../utils/user.utils'

type CompaniesSentInvitationsSortColumn =
  | 'name'
  | 'email'
  | 'relation'
  | 'role'
  | 'jobTitle'
  | 'createdAt'
  | ''

export default function CompaniesSentInvitations() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const companyId = params.companyId!
  const { data: members, isLoading: isLoadingMembers } = useCompanyUsers(
    companyId,
    {
      status: CompanyUsersStatus.PENDING_USER
    }
  )
  const { data: roles, isLoading: isLoadingRoles } = useCompanyRoles(companyId)
  const [searchText, setSearchText] = useState('')
  const [relation, setRelation] = useState('')
  const [role, setRole] = useState('')
  const [orderBy, setOrderBy] = useState<CompaniesSentInvitationsSortColumn>('')
  const [orderDirection, setOrderDirection] = useState<SortDirection>('asc')
  const [filteredMembers, setFilteredMembers] = useState<
    CompanyUserWithUserAndRole[]
  >([])
  const [lastAppliedFilters, setLastAppliedFilters] = useState({
    searchText: '',
    relation: '',
    role: ''
  })
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedMember, setSelectedMember] =
    useState<CompanyUserWithUserAndRole | null>(null)
  const menuOpen = Boolean(anchorEl)
  const { userId } = useAuthStore()
  const { data: user } = useCompanyUser(companyId, userId!)
  const { showSnackbar } = useSnackbar()
  const cancelInvitationMutation = useRejectInvitationMutation(companyId)
  const resendInvitationMutation = useResendInvitationMutation(companyId)
  const hasAppliedFilters = searchText || relation || role

  const { canManageMembers, canCreateMembers } = useMemo(() => {
    return {
      canManageMembers:
        user?.permissions?.includes(Permission.USERS_ACCEPT) ||
        user?.role?.fullAccess,
      canCreateMembers:
        user?.permissions?.includes(Permission.USERS_CREATE) ||
        user?.role?.fullAccess
    }
  }, [user])

  useEffect(() => {
    if (user && !canManageMembers) {
      navigate('/access-denied')
    }
  }, [canManageMembers, navigate, user])

  useEffect(() => {
    setFilteredMembers(members || [])
  }, [members])

  const handleMenuClick = (
    event: MouseEvent<HTMLElement>,
    member: CompanyUserWithUserAndRole
  ) => {
    setAnchorEl(event.currentTarget)
    setSelectedMember(member)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedMember(null)
  }

  const handleResendInvitation = (member: CompanyUserWithUserAndRole) => {
    resendInvitationMutation.mutate(
      {
        companyId,
        userId: member.userId
      },
      {
        onSuccess: () => {
          showSnackbar({
            message: t('resend_invitation_modal.success'),
            severity: 'success'
          })
        },
        onError: () => {
          showSnackbar({
            message: t('resend_invitation_modal.error'),
            severity: 'error'
          })
        }
      }
    )
  }

  const handleCancelInvitation = (member: CompanyUserWithUserAndRole) => {
    cancelInvitationMutation.mutate(
      {
        companyId,
        userId: member.userId
      },
      {
        onSuccess: () => {
          showSnackbar({
            message: t('reject_invitation_modal.success'),
            severity: 'success'
          })
        },
        onError: () => {
          showSnackbar({
            message: t('reject_invitation_modal.error'),
            severity: 'error'
          })
        }
      }
    )
  }

  const handleSort = (column: CompaniesSentInvitationsSortColumn) => {
    let newOrderDirection: SortDirection = 'asc'

    if (orderBy === column) {
      newOrderDirection = orderDirection === 'asc' ? 'desc' : 'asc'
    }

    setOrderBy(column)
    setOrderDirection(newOrderDirection)

    const sorted = [...filteredMembers].sort((a, b) => {
      if (column === 'createdAt') {
        const getDateValue = (member: CompanyUserWithUserAndRole) => {
          return member.createdAt ? new Date(member.createdAt).getTime() : 0
        }
        const valA = getDateValue(a)
        const valB = getDateValue(b)

        return newOrderDirection === 'asc' ? valA - valB : valB - valA
      } else {
        const getStringValue = (member: CompanyUserWithUserAndRole) => {
          switch (column) {
            case 'name':
              return `${member.user.name} ${member.user.lastName}`.toLowerCase()
            case 'email':
              return member.user.email.toLowerCase()
            case 'relation':
              return (member.relation || '').toLowerCase()
            case 'role':
              return member.role.name.toLowerCase()
            case 'jobTitle':
              return (member.jobTitle || '').toLowerCase()
            default:
              return ''
          }
        }
        const valA = getStringValue(a)
        const valB = getStringValue(b)

        return newOrderDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
    })

    setFilteredMembers(sorted)
  }

  const handleFilter = (
    searchValue: string,
    relationValue: string,
    roleValue: string
  ) => {
    const result =
      members?.filter(member => {
        const matchSearch = JSON.stringify(member)
          .toLowerCase()
          .includes(searchValue.toLowerCase())

        const matchRelation = relationValue
          ? member.relation === relationValue
          : true
        const matchRole = roleValue ? member.role.name === roleValue : true

        return matchSearch && matchRelation && matchRole
      }) || []

    setFilteredMembers(result)
    setLastAppliedFilters({
      searchText: searchValue,
      relation: relationValue,
      role: roleValue
    })
  }

  const areFiltersEqual = (
    current: typeof lastAppliedFilters,
    last: typeof lastAppliedFilters
  ) => {
    return (
      current.searchText === last.searchText &&
      current.relation === last.relation &&
      current.role === last.role
    )
  }

  if (isLoadingMembers || isLoadingRoles) {
    return <TableLoader totalColumns={5} totalRows={3} />
  }

  return (
    <>
      {members?.length ? (
        <TableContainer>
          <Stack direction='column' spacing='0.75rem'>
            <Stack
              direction='row'
              justifyContent='space-between'
              sx={{ paddingTop: '1.5rem', paddingBottom: '.75rem' }}
            >
              <Stack direction='row' spacing={2} alignItems='center'>
                <SearchInput
                  searchText={searchText}
                  setSearchText={setSearchText}
                  handleFilter={handleFilter}
                  additionalParams={[relation, role]}
                />
                <Select
                  value={relation}
                  onChange={e => setRelation(e.target.value)}
                  sx={{
                    width: '11.25rem',
                    '& .MuiSelect-select': {
                      padding: '.75rem .75rem'
                    }
                  }}
                  displayEmpty
                  renderValue={selected => {
                    if (selected === '') {
                      return (
                        <Typography color='text.secondary'>
                          {t('common.relation')}
                        </Typography>
                      )
                    }

                    return t(`relation.${selected.toLowerCase()}`)
                  }}
                >
                  <MenuItem value={CompanyUsersRelation.CLIENT}>
                    {t('relation.client')}
                  </MenuItem>
                  <MenuItem value={CompanyUsersRelation.EMPLOYEE}>
                    {t('relation.employee')}
                  </MenuItem>
                  <MenuItem value={CompanyUsersRelation.PROVIDER}>
                    {t('relation.provider')}
                  </MenuItem>
                </Select>
                <Select
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  sx={{
                    width: '11.25rem',
                    '& .MuiSelect-select': {
                      padding: '.75rem .75rem'
                    }
                  }}
                  displayEmpty
                  renderValue={selected => {
                    if (selected === '') {
                      return (
                        <Typography color='text.secondary'>
                          {t('common.role')}
                        </Typography>
                      )
                    }
                    return isDefaultRole(selected)
                      ? t(`role.${selected}`)
                      : selected
                  }}
                >
                  {roles?.map(role => (
                    <MenuItem key={role.id} value={role.name}>
                      {isDefaultRole(role.name)
                        ? t(`role.${role.name}`)
                        : role.name}
                    </MenuItem>
                  ))}
                </Select>

                <Button
                  variant='text'
                  color='primary'
                  onClick={() => handleFilter(searchText, relation, role)}
                  disabled={
                    (!searchText && !relation && !role) ||
                    areFiltersEqual(
                      { searchText, relation, role },
                      lastAppliedFilters
                    )
                  }
                  sx={{ textTransform: 'none', padding: ' 0.375rem 0.5rem' }}
                >
                  {t('common.apply_filters')}
                </Button>
                {hasAppliedFilters && (
                  <Button
                    startIcon={<CloseIcon />}
                    onClick={() => {
                      setSearchText('')
                      setRelation('')
                      setRole('')
                      setFilteredMembers(members || [])
                      setLastAppliedFilters({
                        searchText: '',
                        relation: '',
                        role: ''
                      })
                    }}
                    sx={{
                      textTransform: 'none',
                      color: 'secondary.main'
                    }}
                  >
                    {t('common.delete_filters')}
                  </Button>
                )}
              </Stack>
              {canCreateMembers && (
                <Button
                  variant='text'
                  color='primary'
                  startIcon={<PersonAddAltOutlinedIcon />}
                  component={Link}
                  to={`/companies/${companyId}/invitations/create`}
                  sx={{ textTransform: 'none', fontSize: '1rem' }}
                >
                  {t('members_view.invite_button')}
                </Button>
              )}
            </Stack>

            <Box sx={{ marginTop: '0.75rem' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sortDirection={
                        orderBy === 'name' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={
                          orderBy === 'name' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('name')}
                      >
                        {t('invitations_view.sent.table_headers.user')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        orderBy === 'email' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={
                          orderBy === 'email' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('email')}
                      >
                        {t('invitations_view.sent.table_headers.email')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        orderBy === 'relation' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'relation'}
                        direction={
                          orderBy === 'relation' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('relation')}
                      >
                        {t('invitations_view.sent.table_headers.relation')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        orderBy === 'role' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'role'}
                        direction={
                          orderBy === 'role' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('role')}
                      >
                        {t('invitations_view.sent.table_headers.role')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        orderBy === 'jobTitle' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'jobTitle'}
                        direction={
                          orderBy === 'jobTitle' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('jobTitle')}
                      >
                        {t('invitations_view.sent.table_headers.job_title')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        orderBy === 'createdAt' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'createdAt'}
                        direction={
                          orderBy === 'createdAt' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('createdAt')}
                      >
                        {t('invitations_view.sent.table_headers.created_at')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      {t('invitations_view.sent.table_headers.status')}
                    </TableCell>
                    <TableCell align='right'>
                      {t('invitations_view.sent.table_headers.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMembers.length === 0 && hasAppliedFilters ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <EmptySearchResults />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMembers.map((member, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                          >
                            {member.user.avatarUri ? (
                              <ContainedAvatar
                                src={member.user.avatarUri}
                                alt={member.user.name}
                                sx={{ width: '2.5rem', height: '2.5rem' }}
                              />
                            ) : (
                              <Avatar
                                sx={{ width: '2.5rem', height: '2.5rem' }}
                              >
                                {getAvatarName(
                                  member.user.name || '',
                                  member.user.lastName || ''
                                )}
                              </Avatar>
                            )}
                            <Typography variant='body2'>
                              {member.user.name} {member.user.lastName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {member.user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {member.relation === CompanyUsersRelation.CLIENT && (
                            <Stack
                              direction='row'
                              spacing='.5rem'
                              alignItems='center'
                            >
                              <HandshakeOutlinedIcon
                                fontSize='small'
                                color='action'
                              />
                              <Typography variant='body2'>
                                {t('relation.client')}
                              </Typography>
                            </Stack>
                          )}
                          {member.relation ===
                            CompanyUsersRelation.PROVIDER && (
                            <Stack
                              direction='row'
                              spacing='.5rem'
                              alignItems='center'
                            >
                              <LocalShippingOutlinedIcon
                                fontSize='small'
                                color='action'
                              />
                              <Typography variant='body2'>
                                {t('relation.provider')}
                              </Typography>
                            </Stack>
                          )}
                          {member.relation ===
                            CompanyUsersRelation.EMPLOYEE && (
                            <Stack
                              direction='row'
                              spacing='.5rem'
                              alignItems='center'
                            >
                              <PersonIcon fontSize='small' color='action' />
                              <Typography variant='body2'>
                                {t('relation.employee')}
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <RoleChip role={member.role} />
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {member.jobTitle}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {parseTimestamp(member.createdAt || '')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`status.${member.status}`)}
                            sx={{
                              ...(CompanyUsersStatus.PENDING_USER ===
                                member.status && {
                                backgroundColor: orange[200],
                                color: 'rgba(0, 0, 0, 0.87)'
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <IconButton
                            onClick={e => handleMenuClick(e, member)}
                            sx={{
                              backgroundColor: 'rgba(255, 255, 255, 0.12)'
                            }}
                          >
                            <MoreHorizIcon color='action' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>

            <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    if (selectedMember) {
                      handleResendInvitation(selectedMember)
                    }
                    handleCloseMenu()
                  }}
                >
                  <ListItemIcon>
                    <RefreshOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText>{t('common.resend')}</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (selectedMember) {
                      handleCancelInvitation(selectedMember)
                    }
                    handleCloseMenu()
                  }}
                >
                  <ListItemIcon>
                    <HighlightOffOutlinedIcon color='error' />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>
                    {t('common.cancel')}
                  </ListItemText>
                </MenuItem>
              </MenuList>
            </Menu>
          </Stack>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1.5rem',
            marginTop: '4rem'
          }}
        >
          <img
            src={EmptyInvitations}
            alt='Empty Invitations'
            style={{
              width: '9.5rem',
              height: '9.5rem',
              objectFit: 'contain'
            }}
          />
          <Typography variant='h6' color='text.secondary'>
            {t('invitations_view.sent.empty.title')}
          </Typography>
          <Typography variant='body1'>
            {t('invitations_view.sent.empty.text')}
          </Typography>
          {canCreateMembers && (
            <Button
              variant='contained'
              startIcon={<PersonAddAltOutlinedIcon />}
              component={Link}
              sx={{
                textTransform: 'none',
                color: '#171B1E'
              }}
              to={`/companies/${companyId}/invitations/create`}
            >
              {t('invitations_view.sent.empty.button')}
            </Button>
          )}
        </Box>
      )}
    </>
  )
}
